import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useId } from 'react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'signal'
type Size = 'sm' | 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-magenta text-ink font-bold hover:brightness-110 disabled:bg-disabled disabled:text-dim',
  secondary:
    'bg-dusk text-moon border border-line-strong hover:bg-hover disabled:text-dim disabled:border-line',
  ghost: 'bg-transparent text-haze hover:text-moon hover:bg-dusk disabled:text-dim',
  danger: 'bg-transparent text-danger-text border border-danger hover:bg-danger-bg',
  signal: 'bg-cyan-bg text-cyan-text border border-cyan font-semibold hover:brightness-110',
}

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3 text-[13px] rounded-[10px] gap-1.5',
  md: 'h-11 px-4 text-[15px] rounded-xl gap-2',
  lg: 'h-[52px] px-6 text-base rounded-[14px] gap-2.5',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-[background,filter,color] duration-150',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  )
})

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-haze hover:bg-dusk hover:text-moon',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  )
}

export function EarMark({
  active = true,
  size = 22,
  className,
}: {
  active?: boolean
  size?: number
  className?: string
}) {
  return (
    <svg
      width={size}
      height={(size * 8) / 22}
      viewBox="0 0 22 8"
      aria-hidden
      className={cn(active ? 'text-magenta' : 'text-transparent', className)}
    >
      <path d="M2 8 5 1l4 7Zm11 0 4-7 3 7Z" fill="currentColor" />
    </svg>
  )
}

export function HairStripe({ className }: { className?: string }) {
  return (
    <div className={cn('flex h-1.5 w-40 overflow-hidden rounded-full', className)} aria-hidden>
      <div className="flex-1 bg-rose" />
      <div className="flex-1 bg-magenta" />
      <div className="flex-1 bg-violet" />
    </div>
  )
}

type BadgeTone = 'new' | 'magnet' | 'trailer' | 'solo' | 'muted' | 'warn' | 'ok' | 'bad' | 'run' | 'idle'
const BADGE: Record<BadgeTone, string> = {
  new: 'bg-magenta text-ink font-bold',
  magnet: 'bg-cyan-bg text-cyan-text',
  trailer: 'bg-rose-bg text-rose-soft',
  solo: 'bg-violet-bg text-violet-soft',
  muted: 'border border-line text-haze',
  warn: 'bg-amber-bg text-amber-text',
  ok: 'bg-cyan-bg text-cyan-text',
  bad: 'bg-danger-bg text-danger-text',
  run: 'bg-violet-bg text-violet-soft',
  idle: 'bg-dusk text-haze',
}

export function Badge({
  tone,
  children,
  className,
}: {
  tone: BadgeTone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs whitespace-nowrap',
        BADGE[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

const DOT: Partial<Record<BadgeTone, string>> = {
  ok: 'bg-cyan',
  warn: 'bg-amber',
  bad: 'bg-danger',
  run: 'bg-violet-text',
  idle: 'bg-dim',
}

export function StatusPill({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'bad' | 'run' | 'idle'
  children: ReactNode
}) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs', BADGE[tone])}>
      <span className={cn('size-1.5 rounded-full', DOT[tone])} />
      {children}
    </span>
  )
}

export function QuotaBar({
  used,
  limit,
  tone = 'cyan',
  showRemaining = true,
}: {
  used: number
  limit: number | null
  tone?: 'cyan' | 'magenta'
  showRemaining?: boolean
}) {
  if (limit === null) return <span className="font-mono text-xs text-haze">∞</span>
  const remaining = Math.max(0, limit - used)
  const filled = showRemaining ? remaining : used
  return (
    <span className="inline-flex items-center gap-2">
      <span className="flex gap-1" aria-hidden>
        {Array.from({ length: limit }, (_, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length meter
            key={i}
            className={cn(
              'h-1.5 w-4 rounded-full',
              i < filled ? (tone === 'cyan' ? 'bg-cyan' : 'bg-magenta') : 'bg-line',
            )}
          />
        ))}
      </span>
      <span className="font-mono text-xs text-moon">
        {showRemaining ? remaining : used}/{limit}
      </span>
    </span>
  )
}

export function ToggleChip({
  pressed,
  onPressedChange,
  children,
  tone = 'magenta',
  size = 'md',
}: {
  pressed: boolean
  onPressedChange: (next: boolean) => void
  children: ReactNode
  tone?: 'magenta' | 'cyan'
  size?: 'sm' | 'md'
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-3 whitespace-nowrap transition-colors',
        size === 'md' ? 'h-10 text-sm' : 'h-9 text-[13px]',
        pressed
          ? tone === 'cyan'
            ? 'border-cyan bg-cyan-bg text-cyan-text'
            : 'border-magenta bg-magenta-bg text-rose-soft'
          : 'border-line text-haze hover:border-line-strong hover:text-moon',
      )}
    >
      {children}
    </button>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = 'md',
  className,
}: {
  value: T
  options: { value: T; label: ReactNode }[]
  onChange: (value: T) => void
  label: string
  size?: 'sm' | 'md'
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('inline-flex gap-1 rounded-xl border border-line bg-night p-1', className)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          onClick={() => onChange(o.value)}
          className={cn(
            'flex-1 rounded-[9px] px-3 whitespace-nowrap transition-colors',
            size === 'md' ? 'h-10 text-sm font-semibold' : 'h-8 text-[13px]',
            o.value === value ? 'bg-line text-moon' : 'text-haze hover:text-moon',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  hint?: ReactNode
  error?: string | null
  trailing?: ReactNode
  mono?: boolean
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, trailing, mono, className, id, ...rest },
  ref,
) {
  const auto = useId()
  const inputId = id ?? auto
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label htmlFor={inputId} className="text-sm text-haze">
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-12 w-full rounded-xl border bg-dusk px-3.5 text-[15px] text-moon outline-none transition-colors focus:border-rose',
            error ? 'border-danger' : 'border-line-strong',
            mono && 'font-mono',
            trailing ? 'pr-20' : null,
          )}
          {...rest}
        />
        {trailing ? <div className="absolute inset-y-0 right-1 flex items-center">{trailing}</div> : null}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="text-[13px] text-danger-text">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-faint">
          {hint}
        </p>
      ) : null}
    </div>
  )
})

export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('rounded-[18px] border border-line bg-night', className)}>{children}</section>
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[18px] border border-dashed border-line px-6 py-14 text-center">
      <EarMark size={28} />
      <p className="text-base font-semibold">{title}</p>
      {body ? <p className="max-w-md text-sm leading-relaxed text-haze">{body}</p> : null}
      {action}
    </div>
  )
}

export function ErrorState({
  message,
  onRetry,
  retryLabel,
}: {
  message: string
  onRetry?: () => void
  retryLabel: string
}) {
  return (
    <div
      role="alert"
      className="flex items-center gap-3 rounded-xl border border-danger/60 bg-danger-bg px-4 py-3 text-sm text-danger-text"
    >
      <span className="flex-1">{message}</span>
      {onRetry ? (
        <Button size="sm" variant="danger" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  )
}

export function Notice({
  tone,
  children,
  action,
}: {
  tone: 'warn' | 'ok' | 'info'
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-[14px] border px-4 py-3 text-sm',
        tone === 'warn' && 'border-amber bg-amber-bg text-amber-text',
        tone === 'ok' && 'border-cyan bg-cyan-bg text-cyan-text',
        tone === 'info' && 'border-line bg-deep text-haze',
      )}
    >
      <div className="min-w-0 flex-1 leading-relaxed">{children}</div>
      {action}
    </div>
  )
}
